import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
  CardHeader,
  IconButton,
  Collapse,
  Divider,
  useTheme,
  LinearProgress,
  Avatar,
  Skeleton,
  Button,
  Tooltip,
  Badge,
  alpha,
  keyframes,
} from "@mui/material";
import {
  Search,
  Inventory,
  AttachMoney,
  Warning,
  TrendingUp,
  TrendingDown,
  Factory,
  LocalShipping,
  Science,
  ShoppingCart,
  CheckCircle,
  Cancel,
  Pending,
  Build,
  Assignment,
  Notifications,
  Refresh,
  ExpandMore,
  ExpandLess,
  ArrowUpward,
  ArrowDownward,
  Circle,
  Close,
  ErrorOutline,
  Inbox,
  People,
  Business,
  Percent,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
  LabelList,
} from "recharts";
import CountUp from "react-countup";
import {
  getMainDashboardCounts,
  getMOSPhaseCounts,
  fetchpos,
} from "../../../Services/Services";
import { toast } from "react-toastify";
import InfiniteScroll from "react-infinite-scroll-component";

// ─── Constants ───────────────────────────────────────────────────────────────
const PALETTE = {
  primary: "#1976d2",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#0288d1",
  purple: "#7b1fa2",
  teal: "#00695c",
  indigo: "#303f9f",
};

const CHART_COLORS = [
  "#1976d2",
  "#2e7d32",
  "#ed6c02",
  "#d32f2f",
  "#7b1fa2",
  "#00695c",
  "#0288d1",
  "#303f9f",
];

const DONUT_COLORS = {
  healthy: "#2e7d32",
  low: "#ed6c02",
  out: "#d32f2f",
  critical: "#b71c1c",
};

// ─── Reusable Sub-Components ──────────────────────────────────────────────────
const STEP_COLORS = [
  "#0ea5e9",
  "#2563eb",
  "#4f46e5",
  "#7c3aed",
  "#a855f7",
  "#ec4899",
  "#f43f5e",
  "#10b981",
];
const SectionTitle = ({ title, subtitle, action }) => (
  <Box
    display="flex"
    alignItems={{ xs: "flex-start", sm: "center" }}
    justifyContent="space-between"
    flexDirection={{ xs: "column", sm: "row" }}
    gap={1}
    mb={2}
  >
    <Box>
      <Typography
        variant="h6"
        fontWeight={700}
        color="text.primary"
        sx={{ letterSpacing: "-0.3px" }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    {action && action}
  </Box>
);

const StatChip = ({ value, positive }) => (
  <Chip
    size="small"
    icon={
      positive ? (
        <ArrowUpward sx={{ fontSize: "11px !important" }} />
      ) : (
        <ArrowDownward sx={{ fontSize: "11px !important" }} />
      )
    }
    label={`${value}`}
    sx={{
      height: 20,
      fontSize: "0.65rem",
      fontWeight: 700,
      bgcolor: positive
        ? alpha(PALETTE.success, 0.12)
        : alpha(PALETTE.error, 0.12),
      color: positive ? PALETTE.success : PALETTE.error,
      "& .MuiChip-icon": {
        color: positive ? PALETTE.success : PALETTE.error,
      },
    }}
  />
);

const KpiCard = ({ card, index = 0 }) => (
  <Card
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2.5,
      height: "100%",
      position: "relative",
      overflow: "hidden",
      bgcolor: "background.paper",
      transition: "border-color 0.3s ease",
      "&:hover": {
        borderColor: card.color,
      },
      "&:hover .kpi-icon": {
        transform: "rotate(-15deg) scale(1.15)",
        bgcolor: card.color,
        color: "#fff",
      },
    }}
  >
    <CardContent sx={{ p: "8px 10px 8px 12px", "&:last-child": { pb: "8px" } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box flex={1} minWidth={0}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontSize: "0.6rem",
              display: "block",
              mb: 0.3,
            }}
          >
            {card.title}
          </Typography>
          <Typography
            variant="h5"
            fontWeight={800}
            color="text.primary"
            sx={{
              lineHeight: 1.15,
              letterSpacing: "-0.5px",
              fontSize: { xs: "1.3rem", md: "1.45rem" },
            }}
          >
            {card.isCurrency && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.52em",
                  fontWeight: 700,
                  mr: 0.3,
                  color: "text.secondary",
                }}
              >
                ₹
              </Box>
            )}
            <CountUp end={card.value} duration={1.4} separator="," />
            {card.isPercent && (
              <Box
                component="span"
                sx={{
                  fontSize: "0.52em",
                  fontWeight: 700,
                  ml: 0.3,
                  color: "text.secondary",
                }}
              >
                %
              </Box>
            )}
          </Typography>
        </Box>
        <Avatar
          className="kpi-icon"
          sx={{
            bgcolor: alpha(card.color, 0.12),
            color: card.color,
            width: 40,
            height: 40,
            ml: 1,
            transition: "all 0.35s ease",
          }}
        >
          {card.icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const ProgressRow = ({ label, value, max, color, count }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <Box mb={1.5}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={0.4}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        <Typography variant="caption" fontWeight={700} color="text.primary">
          {count}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 6,
          borderRadius: 4,
          bgcolor: alpha(color, 0.12),
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
};

const moveLine = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 24px 0; }
`;

const WorkflowStep = ({ label, count, color, isLast }) => (
  <Box
    display="flex"
    alignItems="flex-start"
    flex={isLast ? "0 0 auto" : "1 1 0"}
    minWidth={0} // shrink allow — horizontal scroll band
  >
    {/* Node */}
    <Box
      position="relative"
      display="flex"
      justifyContent="center"
      flexShrink={0}
      zIndex={2}
    >
      <Avatar
        sx={{
          bgcolor: alpha(color, 0.12),
          color,
          width: 44,
          height: 44,
          border: `2px solid ${color}`,
          boxShadow: `0 4px 10px ${alpha(color, 0.15)}`,
          transition: "transform 0.2s",
          "&:hover": { transform: "scale(1.1)" },
        }}
      >
        <Typography variant="subtitle2" fontWeight={800}>
          {count}
        </Typography>
      </Avatar>

      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{
          position: "absolute",
          top: 50,
          left: "50%",
          transform: "translateX(-50%)",
          width: 90,
          textAlign: "center",
          fontSize: "0.62rem",
          textTransform: "uppercase",
          letterSpacing: "0.4px",
          lineHeight: 1.2,
          // lamba naam ho to truncate
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>

    {/* Line + Arrow — remaining space fill */}
    {!isLast && (
      <Box
        display="flex"
        alignItems="center"
        flex={1}
        minWidth={8}
        mt="20px"
        zIndex={1}
        px={0.5}
      >
        <Box height={3} flex={1} bgcolor={color} borderRadius={1} />
        <Box
          sx={{
            width: 0,
            height: 0,
            flexShrink: 0,
            borderTop: "5px solid transparent",
            borderBottom: "5px solid transparent",
            borderLeft: `7px solid ${color}`,
          }}
        />
      </Box>
    )}
  </Box>
);


const NotificationItem = ({ icon, message, type, time }) => {
  const colorMap = {
    warning: PALETTE.warning,
    error: PALETTE.error,
    info: PALETTE.primary,
    success: PALETTE.success,
  };
  const color = colorMap[type] || PALETTE.primary;
  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1.5}
      py={1.35}
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Avatar
        sx={{ bgcolor: alpha(color, 0.12), color, width: 34, height: 34 }}
      >
        {icon}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.primary"
          sx={{ fontSize: "0.8rem", lineHeight: 1.35 }}
        >
          {message}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {time}
        </Typography>
      </Box>
    </Box>
  );
};

const TimelineItem = ({ icon, title, subtitle, color, isLast }) => (
  <Box display="flex" gap={1.5} pb={isLast ? 0 : 2.2} position="relative">
    {!isLast && (
      <Box
        sx={{
          position: "absolute",
          left: 15,
          top: 34,
          width: 2,
          bottom: 0,
          bgcolor: "divider",
        }}
      />
    )}
    <Avatar
      sx={{
        bgcolor: alpha(color, 0.12),
        color,
        width: 32,
        height: 32,
        flexShrink: 0,
        zIndex: 1,
      }}
    >
      {icon}
    </Avatar>
    <Box pt={0.3} minWidth={0}>
      <Typography
        variant="body2"
        fontWeight={600}
        color="text.primary"
        sx={{ fontSize: "0.8rem" }}
      >
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const StatusMetricRow = ({ icon, label, value, color, isLast }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    py={1.15}
    sx={{
      borderBottom: isLast ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Box display="flex" alignItems="center" gap={1.2}>
      <Avatar
        sx={{
          bgcolor: alpha(color, 0.12),
          color,
          width: 28,
          height: 28,
        }}
      >
        {icon}
      </Avatar>
      <Typography
        variant="body2"
        fontWeight={500}
        color="text.primary"
        sx={{ fontSize: "0.8rem" }}
      >
        {label}
      </Typography>
    </Box>
    <Typography
      variant="body2"
      fontWeight={700}
      color="text.primary"
      sx={{ fontSize: "0.85rem", minWidth: 18, textAlign: "right" }}
    >
      {value}
    </Typography>
  </Box>
);

const ProgressSummaryCard = ({ title, subtitle, rows, footer }) => {
  const max = Math.max(0, ...rows.map((r) => Number(r.count) || 0));
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 2.5 },
        }}
      >
        <SectionTitle title={title} subtitle={subtitle} />
        <Box flex={1}>
          {rows.map((row) => (
            <ProgressRow
              key={row.label}
              label={row.label}
              value={row.count}
              max={max}
              color={row.color}
              count={row.count}
            />
          ))}
        </Box>
        {footer && (
          <Box
            mt={1}
            px={1.5}
            py={1.1}
            bgcolor={alpha("#90a4ae", 0.1)}
            borderRadius={2}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.72rem", lineHeight: 1.45 }}
            >
              {footer}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const StatusListCard = ({ title, subtitle, rows, footer }) => (
  <Card
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 3,
      height: "100%",
      bgcolor: "background.paper",
    }}
  >
    <CardContent
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:last-child": { pb: 2.5 },
      }}
    >
      <SectionTitle title={title} subtitle={subtitle} />
      <Box flex={1}>
        {rows.map((row, i) => (
          <StatusMetricRow
            key={row.label}
            {...row}
            isLast={i === rows.length - 1}
          />
        ))}
      </Box>
      {footer && (
        <Box
          mt={2}
          px={1.5}
          py={1.1}
          bgcolor={alpha("#90a4ae", 0.1)}
          borderRadius={2}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.72rem", lineHeight: 1.45 }}
          >
            {footer}
          </Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// ─── Skeleton Loaders ─────────────────────────────────────────────────────────

const KpiSkeleton = () => (
  <Card
    elevation={0}
    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" justifyContent="space-between">
        <Box flex={1}>
          <Skeleton width="60%" height={14} sx={{ mb: 1 }} />
          <Skeleton width="40%" height={36} sx={{ mb: 0.5 }} />
          <Skeleton width="50%" height={12} />
        </Box>
        <Skeleton variant="circular" width={48} height={48} />
      </Box>
    </CardContent>
  </Card>
);

const CardSkeleton = ({ height = 200 }) => (
  <Card
    elevation={0}
    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Skeleton width="40%" height={20} sx={{ mb: 2 }} />
      <Skeleton
        variant="rectangular"
        height={height}
        sx={{ borderRadius: 2 }}
      />
    </CardContent>
  </Card>
);

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        boxShadow: 3,
      }}
    >
      <Typography variant="caption" fontWeight={700} color="text.primary">
        {label}
      </Typography>
      {payload.map((p, i) => (
        <Typography
          key={i}
          variant="caption"
          display="block"
          color="text.secondary"
        >
          {p.name}:{" "}
          <strong style={{ color: p.fill || p.color }}>
            ₹{new Intl.NumberFormat("en-IN").format(p.value)}
          </strong>
        </Typography>
      ))}
    </Box>
  );
};

const CustomDonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        boxShadow: 3,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        color={payload[0].payload.fill}
      >
        {payload[0].name}: {payload[0].value}
      </Typography>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const DashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [searchTerm, setSearchTerm] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sizeWiseStock, setSizeWiseStock] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [phases, setPhases] = useState([]);
  const [phasesLoading, setPhasesLoading] = useState(false); // ✅ YEH ADD KARO
  const [todayPurchaseOrders, setTodayPurchaseOrders] = useState(0);
  const [poStatusCounts, setPoStatusCounts] = useState({
    pending: 0,
    approved: 0,
    received: 0,
    cancelled: 0,
  });

  // ── Data Fetching ──────────────────────────────────────────────────────────

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setHasError(false);
      const response = await getMainDashboardCounts();
      setDashboardData(response);
      setSizeWiseStock(response.size_wise_stock || []);
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to load dashboard data");
      setHasError(true);
    } finally {
      setLoading(false);
    }
    // 2. Fetch phases with real MOS counts
    setPhasesLoading(true);
    try {
      const phaseCounts = await getMOSPhaseCounts();
      // Backend se already sorted data aa raha hai, frontend sort ki zarurat nahi hai.
      setPhases(phaseCounts);
    } catch (err) {
      console.warn("Phases fetch failed:", err);
    } finally {
      setPhasesLoading(false);
    }

    // 3. Fetch Today's Purchase Orders count
    try {
      const poData = await fetchpos({ date_range: "today" }, 1);
      setTodayPurchaseOrders(poData.count || 0);
    } catch (err) {
      console.warn("Failed to fetch today's POs:", err);
    }

    try {
      const [pending, approved, received, cancelled] = await Promise.all([
        fetchpos({ status: "pending" }, 1),
        fetchpos({ status: "approved" }, 1),
        fetchpos({ status: "received" }, 1),
        fetchpos({ status: "cancelled" }, 1),
      ]);
      setPoStatusCounts({
        pending: pending?.count || 0,
        approved: approved?.count || 0,
        received: received?.count || 0,
        cancelled: cancelled?.count || 0,
      });
    } catch (err) {
      console.warn("PO status counts failed:", err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // ── Formatters ─────────────────────────────────────────────────────────────

  const formatCurrency = useCallback(
    (value) =>
      new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0),
    [],
  );

  const formatTimeAgo = useCallback((iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return "Just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hrs ago`;
    const day = Math.floor(hr / 24);
    if (day === 1) return "Yesterday";
    if (day < 7) return `${day} days ago`;
    return d.toLocaleDateString("en-GB");
  }, []);

  // ── Derived Data ───────────────────────────────────────────────────────────

  const {
    summary = {},
    stockByCategory = [],
    stockStatus = {},
    monthlyTrend = [],
    items = [],
    pendingOrders = [],
    recentActivities: recentActivityApi = [],
  } = useMemo(() => dashboardData || {}, [dashboardData]);
  const filteredItems = useMemo(
    () =>
      items.filter(
        (item) =>
          (item.name &&
            item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.sku &&
            item.sku.toLowerCase().includes(searchTerm.toLowerCase())),
      ),
    [items, searchTerm],
  );

  const totalSizeWiseValue = useMemo(
    () => sizeWiseStock.reduce((acc, s) => acc + (s.total_stock_value || 0), 0),
    [sizeWiseStock],
  );

  // ── KPI Cards Definition ───────────────────────────────────────────────────

  const kpiCards = useMemo(
    () => [
      {
        title: "Total Creditors",
        value: summary.totalCreditors || 0,
        sub: "Vendors",
        icon: <Business fontSize="small" />,
        color: PALETTE.teal,
        trend: undefined,
      },
      {
        title: "Total Debtors",
        value: summary.totalDebtors || 0,
        sub: "Parties",
        icon: <People fontSize="small" />,
        color: PALETTE.primary,
        trend: undefined,
      },
      {
        title: "Total Enquiries",
        value: summary.totalEnquiries || 0,
        sub: "All enquiries",
        icon: <Assignment fontSize="small" />,
        color: PALETTE.indigo,
        trend: undefined,
      },
      {
        title: "Success Rate",
        value: summary.quotationSuccessRate || 0,
        sub: "Quotations approved",
        icon: <TrendingUp fontSize="small" />,
        color: PALETTE.success,
        isPercent: true,
        trend: undefined,
      },
      {
        title: "Low Stock Materials",
        value: summary.lowStockItems || 0,
        sub: "Require reorder",
        icon: <Warning fontSize="small" />,
        color: PALETTE.error,
        trend: undefined,
      },
      {
        title: "Today's Purchase Orders",
        value: todayPurchaseOrders, // TODO: Connect API — summary.todayPurchaseOrders
        sub: "New POs today",
        icon: <ShoppingCart fontSize="small" />,
        color: PALETTE.warning,
        trend: undefined,
      },
      {
        title: "Total Stock Value",
        value: summary.totalInventoryValue || 0,
        sub: "All warehouses",
        icon: <AttachMoney fontSize="small" />,
        color: PALETTE.primary,
        isCurrency: true,
        trend: undefined,
      },
      {
        title: "Total Stock Items",
        value: summary.totalItems || 0,
        sub: "Active SKUs",
        icon: <Inventory fontSize="small" />,
        color: PALETTE.success,
        trend: undefined,
      },
    ],
    [summary, todayPurchaseOrders],
  );

  // ── Inventory Health Donut Data ────────────────────────────────────────────

  const inventoryHealthData = useMemo(() => {
    const healthy = stockStatus.healthy || 0;
    const low = stockStatus.low || summary.lowStockItems || 0;
    const out = stockStatus.outOfStock || 0;
    const critical = stockStatus.critical || 0;
    const total = healthy + low + out + critical || 1;
    return [
      {
        name: "Healthy",
        value: healthy,
        fill: DONUT_COLORS.healthy,
        pct: ((healthy / total) * 100).toFixed(1),
      },
      {
        name: "Low Stock",
        value: low,
        fill: DONUT_COLORS.low,
        pct: ((low / total) * 100).toFixed(1),
      },
      {
        name: "Out of Stock",
        value: out,
        fill: DONUT_COLORS.out,
        pct: ((out / total) * 100).toFixed(1),
      },
      {
        name: "Critical",
        value: critical,
        fill: DONUT_COLORS.critical,
        pct: ((critical / total) * 100).toFixed(1),
      },
    ];
  }, [stockStatus, summary]);

  // TODO: Connect API — placeholder workflow counts
  const groupedWorkflowSteps = useMemo(() => {
    const grouped = {};
    phases.forEach((phase) => {
      const wtName = phase.work_type_name;

      // Ignore null, empty, or "General" work types taaki wo faltu me render na ho
      if (!wtName || wtName === "General") return;

      if (!grouped[wtName]) {
        grouped[wtName] = [];
      }
      grouped[wtName].push({
        label: phase.phase_name,
        count: phase.count || 0,
        color: STEP_COLORS[grouped[wtName].length % STEP_COLORS.length],
      });
    });
    return Object.entries(grouped);
  }, [phases]);

  // TODO: Connect API — Dispatch counts
  const dispatchRows = useMemo(
    () => [
      {
        icon: <LocalShipping sx={{ fontSize: 16 }} />,
        label: "Today's Dispatch",
        value: summary.todayDispatch || 0,
        color: PALETTE.info,
      },
      {
        icon: <Pending sx={{ fontSize: 16 }} />,
        label: "Pending",
        value: summary.dispatchPending || 0,
        color: PALETTE.warning,
      },
      {
        icon: <Warning sx={{ fontSize: 16 }} />,
        label: "Delayed",
        value: summary.dispatchDelayed || 0,
        color: PALETTE.error,
      },
      {
        icon: <CheckCircle sx={{ fontSize: 16 }} />,
        label: "Completed",
        value: summary.dispatchCompleted || 0,
        color: PALETTE.success,
      },
    ],
    [summary],
  );

  // TODO: Connect API — Production summary counts
  const productionRows = useMemo(
    () => [
      {
        label: "Pending",
        count: summary.pendingPOs || 0,
        color: PALETTE.warning,
      },
      {
        label: "Approved",
        count: summary.approvedPOs || 0,
        color: PALETTE.teal,
      },
      {
        label: "Received",
        count: summary.receivedPOs || 0,
        color: PALETTE.success,
      },
      {
        label: "Cancelled",
        count: summary.cancelledPOs || 0,
        color: PALETTE.error,
      },
    ],
    [summary],
  );

  // TODO: Connect API — Purchase summary counts
  const quotationRows = useMemo(
    () => [
      {
        label: "Approved",
        count: summary.approvedQuotations || 0,
        color: PALETTE.success,
      },
      {
        label: "Pending",
        count: summary.pendingQuotations || 0,
        color: PALETTE.warning,
      },
      {
        label: "Cancelled",
        count: summary.cancelledQuotations || 0,
        color: PALETTE.error,
      },
    ],
    [summary],
  );

  // TODO: Connect API — placeholder recent activities
  const recentActivities = useMemo(() => {
    const iconMap = {
      quotation: {
        icon: <Assignment sx={{ fontSize: 16 }} />,
        color: PALETTE.primary,
      },
      sales_order: {
        icon: <Factory sx={{ fontSize: 16 }} />,
        color: PALETTE.indigo,
      },
      purchase_order: {
        icon: <ShoppingCart sx={{ fontSize: 16 }} />,
        color: PALETTE.success,
      },
    };
    return (recentActivityApi || []).map((a) => {
      const meta = iconMap[a.type] || iconMap.quotation;
      return {
        icon: meta.icon,
        title: a.title,
        subtitle: `${a.name || "—"} • ${formatTimeAgo(a.created_at)}`,
        color: meta.color,
      };
    });
  }, [recentActivityApi, formatTimeAgo]);

  // ── Loading State ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, mt: { xs: 5, md: 0 } }}>
        <Box mb={3}>
          <Skeleton width={220} height={32} />
          <Skeleton width={160} height={18} sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={2.5} mb={3}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <KpiSkeleton />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2.5} mb={3}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} md={4} key={i}>
              <CardSkeleton height={180} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={8}>
            <CardSkeleton height={300} />
          </Grid>
          <Grid item xs={12} md={4}>
            <CardSkeleton height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  // ── Error State ────────────────────────────────────────────────────────────

  if (hasError || !dashboardData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
          p: 3,
          mt: { xs: 5, md: 0 },
        }}
      >
        <Avatar
          sx={{
            bgcolor: alpha(PALETTE.error, 0.1),
            color: PALETTE.error,
            width: 64,
            height: 64,
          }}
        >
          <ErrorOutline sx={{ fontSize: 36 }} />
        </Avatar>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Failed to Load Dashboard
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          maxWidth={320}
        >
          There was an error connecting to the server. Please check your
          connection and try again.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={fetchDashboardData}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        mt: { xs: 5, md: 0 },
        minHeight: "100vh",
        bgcolor: "background.default",
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 2.5 },
      }}
    >
      {/* ── KPI Cards Row ─────────────────────────────────────────────────── */}
      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        {kpiCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <KpiCard card={card} index={idx} />
          </Grid>
        ))}
      </Grid>

      {/* ── Manufacturing Workflow Pipeline ───────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          mb: 3,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <SectionTitle
            title="Manufacturing Workflow Pipeline"
            subtitle="Current orders by production stage & work type"
          />
          {phasesLoading ? (
            <Box display="flex" gap={2} py={2}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="circular" width={44} height={44} />
              ))}
            </Box>
          ) : groupedWorkflowSteps.length > 0 ? (
            <Box display="flex" flexDirection="column" gap={0}>
              {groupedWorkflowSteps.map(([workType, steps], idx) => (
                <Box
                  key={idx}
                  mb={idx !== groupedWorkflowSteps.length - 1 ? 4 : 0}
                >
                  {/* Premium Header for WorkType */}
                  <Box mb={2.5} display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 26,
                        height: 26,
                        bgcolor: alpha(PALETTE.indigo, 0.1),
                        color: PALETTE.indigo,
                      }}
                    >
                      <Factory sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Typography
                      variant="subtitle2"
                      fontWeight={800}
                      color="text.primary"
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {workType}
                    </Typography>
                    <Box
                      flex={1}
                      height={2}
                      bgcolor={alpha(PALETTE.indigo, 0.08)}
                      ml={1}
                      borderRadius={2}
                    />
                  </Box>

                  {/* Nodes & Animated Lines Container */}
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    width="100%"
                    sx={{ overflowX: "hidden", pb: 2, pt: 1, px: 0.5 }}
                  >
                    {steps.map((step, i) => (
                      <WorkflowStep
                        key={i}
                        {...step}
                        isLast={i === steps.length - 1}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              mt={1}
              px={1.5}
              py={1.2}
              bgcolor={alpha(PALETTE.warning, 0.08)}
              borderRadius={2}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                No active workflows found. Create phases assigned to a Work Type
                to see them here.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Production + Purchase + Inventory Health ──────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <ProgressSummaryCard
            title="Production Summary"
            subtitle="Current order status"
            rows={productionRows}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <ProgressSummaryCard
            title="Quotation Summary"
            subtitle="Quotation status breakdown"
            rows={quotationRows}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionTitle
                title="Inventory Health"
                subtitle="Stock status distribution"
              />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
              >
                <Box sx={{ width: 160, height: 160, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={inventoryHealthData}
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={72}
                        paddingAngle={3}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {inventoryHealthData.map((entry, index) => (
                          <Cell key={index} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomDonutTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                {/* Legend - Below Donut */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 0.5,
                    width: "100%",
                    maxWidth: 300,
                  }}
                >
                  {inventoryHealthData.map((d, i) => (
                    <Box
                      key={i}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: 1,
                        bgcolor: alpha(d.fill, 0.08),
                        border: `1px solid ${alpha(d.fill, 0.2)}`,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={0.6}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: d.fill,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                        >
                          {d.name}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.primary"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {d.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Recent Activities + Stock + Order ───────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
              bgcolor: "background.paper",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionTitle
                title="Recent Activities"
                subtitle="Latest events"
              />
              {recentActivities.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent activity.
                </Typography>
              ) : (
                <Box>
                  {recentActivities.map((item, i) => (
                    <TimelineItem
                      key={i}
                      {...item}
                      isLast={i === recentActivities.length - 1}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <SectionTitle
                title="Stock Value by Category"
                subtitle="Inventory value distribution"
              />
              {stockByCategory.length > 0 ? (
                <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
                  <Box
                    sx={{
                      minWidth: Math.max(stockByCategory.length * 120, 350),
                      height: 300,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={stockByCategory}
                        margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={alpha("#000", 0.08)}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="name"
                          angle={-45}
                          textAnchor="end"
                          interval={0}
                          height={80}
                          tick={{
                            fontSize: 11,
                            fill: theme.palette.text.primary,
                            fontWeight: 500,
                          }}
                          tickLine={false}
                          axisLine={{ stroke: alpha("#000", 0.15) }}
                        />
                        <YAxis
                          tickFormatter={(v) => {
                            if (v >= 10000000)
                              return `₹${(v / 10000000).toFixed(1)}Cr`;
                            if (v >= 100000)
                              return `₹${(v / 100000).toFixed(1)}L`;
                            if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
                            return `₹${v}`;
                          }}
                          tick={{
                            fontSize: 10,
                            fill: theme.palette.text.secondary,
                          }}
                          axisLine={false}
                          tickLine={false}
                          width={80}
                        />
                        <RechartsTooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            return (
                              <Box
                                sx={{
                                  bgcolor: "background.paper",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 2,
                                  p: 1.5,
                                  boxShadow: 3,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  color="text.primary"
                                  sx={{
                                    fontSize: "0.75rem",
                                    mb: 0.5,
                                    display: "block",
                                  }}
                                >
                                  {label}
                                </Typography>
                                {payload.map((p, i) => (
                                  <Typography
                                    key={i}
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.7rem" }}
                                  >
                                    Value:{" "}
                                    <strong
                                      style={{ color: p.fill || p.color }}
                                    >
                                      ₹
                                      {new Intl.NumberFormat("en-IN").format(
                                        p.value,
                                      )}
                                    </strong>
                                  </Typography>
                                ))}
                              </Box>
                            );
                          }}
                        />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={50}>
                          {stockByCategory.map((entry, index) => (
                            <Cell
                              key={index}
                              fill={CHART_COLORS[index % CHART_COLORS.length]}
                            />
                          ))}
                          <LabelList
                            dataKey="value"
                            position="top"
                            formatter={(v) => {
                              if (v >= 10000000)
                                return `₹${(v / 10000000).toFixed(1)}Cr`;
                              if (v >= 100000)
                                return `₹${(v / 100000).toFixed(1)}L`;
                              if (v >= 1000)
                                return `₹${(v / 1000).toFixed(0)}K`;
                              return `₹${v}`;
                            }}
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              fill: theme.palette.text.primary,
                            }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height={250}
                  gap={1}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(PALETTE.primary, 0.08),
                      color: PALETTE.primary,
                      width: 48,
                      height: 48,
                    }}
                  >
                    <Inbox />
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">
                    No category data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <StatusListCard
            title="Order"
            subtitle="Delivery status"
            rows={dispatchRows}
          />
        </Grid>
      </Grid>
      {/* ── Material Value Cards ───────────────────────────────────────────── */}
      {sizeWiseStock.length > 0 && (
        <Box mb={3}>
          <Box mb={2}>
            <Typography
              variant="h6"
              fontWeight={700}
              color="text.primary"
              sx={{ letterSpacing: "-0.3px" }}
            >
              Material Value Breakdown
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Stock value by material type
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {sizeWiseStock.map((item) => {
              const pct =
                totalSizeWiseValue > 0
                  ? (
                      (item.total_stock_value / totalSizeWiseValue) *
                      100
                    ).toFixed(1)
                  : 0;
              return (
                <Grid item xs={12} sm={6} md={3} key={item.id}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 3,
                      height: "100%",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        borderColor: PALETTE.success,
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px -4px ${alpha(PALETTE.success, 0.15)}`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontSize: "0.62rem",
                          }}
                        >
                          {item.size}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${pct}%`}
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            bgcolor: alpha(PALETTE.primary, 0.08),
                            color: PALETTE.primary,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        color={PALETTE.success}
                        sx={{
                          letterSpacing: "-0.5px",
                          lineHeight: 1.1,
                          mb: 0.5,
                        }}
                      >
                        ₹{formatCurrency(item.total_stock_value)}
                      </Typography>

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mt={1}
                        pt={1}
                        sx={{ borderTop: "1px solid", borderColor: "divider" }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Stock Qty
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.primary"
                        >
                          {item.total_stock}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(pct)}
                        sx={{
                          mt: 1,
                          height: 4,
                          borderRadius: 4,
                          bgcolor: alpha(PALETTE.success, 0.1),
                          "& .MuiLinearProgress-bar": {
                            bgcolor: PALETTE.success,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* ── Pending Manufacturing Orders Table (TODO) ──────────────────────── */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <SectionTitle
            title="Pending Manufacturing Orders"
            subtitle={`${pendingOrders.length} pending MOS`}
          />
          {pendingOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pending orders.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "S.No",
                      "SO No",
                      "Debtor",
                      "MOS",
                      "Delivery Date",
                      "Amount",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{ fontWeight: 700, fontSize: "0.7rem" }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingOrders.map((row, i) => (
                    <TableRow key={row.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{row.so_no || "—"}</TableCell>
                      <TableCell>{row.party_name || "—"}</TableCell>
                      <TableCell>{row.project_name || "—"}</TableCell>
                      <TableCell>
                        {row.delivery_date
                          ? new Date(row.delivery_date).toLocaleDateString(
                              "en-GB",
                            )
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {row.grand_total
                          ? Number(row.grand_total).toLocaleString("en-IN")
                          : "0"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Low Stock Materials Table ──────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Header Row */}
          <Box
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={1.5}
            mb={2}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="text.primary"
                sx={{ letterSpacing: "-0.3px" }}
              >
                Low Stock Materials
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {filteredItems.length} items found — materials requiring reorder
              </Typography>
            </Box>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Search by name or SKU…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "background.paper",
                  fontSize: "0.82rem",
                  "& fieldset": { borderColor: "divider" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      edge="end"
                    >
                      <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {/* Table / Mobile Cards */}
          <InfiniteScroll
            dataLength={items.length}
            loader={
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            }
          >
            {isMobile ? (
              /* ── Mobile Cards ── */
              <Box>
                {filteredItems.length === 0 ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={5}
                    gap={1}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(PALETTE.primary, 0.08),
                        color: PALETTE.primary,
                      }}
                    >
                      <Inbox />
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">
                      No items match your search.
                    </Typography>
                  </Box>
                ) : (
                  filteredItems.map((row, index) => (
                    <Box key={index}>
                      <Box
                        onClick={() =>
                          setExpandedItem(expandedItem === index ? null : index)
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1.5,
                          py: 1.2,
                          cursor: "pointer",
                          bgcolor:
                            index % 2 === 0
                              ? alpha(PALETTE.primary, 0.02)
                              : "background.paper",
                          borderLeft: `3px solid ${PALETTE.primary}`,
                          transition: "background 0.15s",
                          "&:active": { bgcolor: alpha(PALETTE.primary, 0.07) },
                        }}
                      >
                        <Box flex={1}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.8}
                            mb={0.3}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "text.secondary",
                                fontWeight: 600,
                                minWidth: 22,
                              }}
                            >
                              #{index + 1}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.82rem",
                                color: "text.primary",
                                flex: 1,
                              }}
                            >
                              {row.name || "N/A"}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              color: "text.secondary",
                              ml: "30px",
                            }}
                          >
                            {row.category_name?.substring(0, 28) || "N/A"}
                            {row.category_name?.length > 28 ? "…" : ""}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            size="small"
                            label={`Qty: ${row.current_stock}`}
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor:
                                row.current_stock <= row.min_quantity
                                  ? alpha(PALETTE.error, 0.1)
                                  : alpha(PALETTE.success, 0.1),
                              color:
                                row.current_stock <= row.min_quantity
                                  ? PALETTE.error
                                  : PALETTE.success,
                            }}
                          />
                          <IconButton size="small" sx={{ p: 0.3 }}>
                            {expandedItem === index ? (
                              <ExpandLess fontSize="small" />
                            ) : (
                              <ExpandMore fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </Box>

                      <Collapse
                        in={expandedItem === index}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderLeft: `3px solid ${PALETTE.primary}`,
                            bgcolor: alpha(PALETTE.primary, 0.03),
                          }}
                        >
                          <Grid container spacing={1.5}>
                            {[
                              {
                                label: "Product Type",
                                value: row.size_name || row.sku || "—",
                                color: PALETTE.primary,
                              },
                              {
                                label: "Stock Qty",
                                value: row.current_stock,
                                color: PALETTE.success,
                              },
                              {
                                label: "Min Qty",
                                value: row.min_quantity,
                                color: PALETTE.error,
                              },
                              {
                                label: "Max Qty",
                                value: row.max_quantity,
                                color: "text.primary",
                              },
                              {
                                label: "Unit",
                                value: row.unit_name || "—",
                                color: "text.primary",
                              },
                            ].map((field, fi) => (
                              <Grid item xs={6} key={fi}>
                                <Typography
                                  sx={{
                                    fontSize: "0.62rem",
                                    color: "text.secondary",
                                    letterSpacing: "0.4px",
                                    mb: 0.2,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {field.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.78rem",
                                    fontWeight: 700,
                                    color: field.color,
                                  }}
                                >
                                  {field.value}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Collapse>
                      <Divider />
                    </Box>
                  ))
                )}
              </Box>
            ) : (
              /* ── Desktop Table ── */
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  maxHeight: "52vh",
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  "&::-webkit-scrollbar": { width: 6, height: 6 },
                  "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#bdbdbd",
                    borderRadius: 3,
                  },
                }}
              >
                <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { label: "S.No", width: 40, align: "center" },
                        { label: "Item Name", width: 300, align: "left" },
                        { label: "Category", width: 140, align: "left" },
                        { label: "Product Type", width: 120, align: "center" },
                        { label: "Unit", width: 70, align: "center" },
                        { label: "Stock Qty", width: 90, align: "center" },
                        { label: "Min Qty", width: 80, align: "center" },
                        { label: "Status", width: 90, align: "center" },
                      ].map((col, ci) => (
                        <TableCell
                          key={ci}
                          align={col.align}
                          sx={{
                            width: col.width,
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? alpha(t.palette.common.white, 0.06)
                                : alpha(PALETTE.primary, 0.04),
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            letterSpacing: "0.4px",
                            textTransform: "uppercase",
                            color: "text.secondary",
                            py: 1.2,
                            px: 1.5,
                            whiteSpace: "nowrap",
                            borderBottom: "2px solid",
                            borderColor: "divider",
                          }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={1}
                          >
                            <Avatar
                              sx={{
                                bgcolor: alpha(PALETTE.primary, 0.08),
                                color: PALETTE.primary,
                              }}
                            >
                              <Inbox />
                            </Avatar>
                            <Typography variant="body2" color="text.secondary">
                              No items match your search.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredItems.map((row, index) => {
                        const isLow = row.current_stock <= row.min_quantity;
                        const isOut = row.current_stock === 0;
                        return (
                          <TableRow
                            hover
                            key={index}
                            sx={{
                              bgcolor:
                                index % 2 === 0
                                  ? alpha(theme.palette.primary.main, 0.04)
                                  : "background.paper",
                              "&:hover": {
                                bgcolor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
                              },
                              transition: "background 0.15s",
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "0.72rem",
                                color: "text.secondary",
                                fontWeight: 600,
                                py: 0.8,
                                px: 1.5,
                              }}
                            >
                              {index + 1}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "text.primary",
                                py: 0.8,
                                px: 1.5,
                                maxWidth: 300,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.name || "—"}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: "0.72rem",
                                color: "text.secondary",
                                py: 0.8,
                                px: 1.5,
                                maxWidth: 140,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.category_name || "—"}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "0.72rem",
                                color: "text.secondary",
                                py: 0.8,
                                px: 1.5,
                              }}
                            >
                              {row.size_name || "—"}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "0.72rem",
                                color: "text.secondary",
                                py: 0.8,
                                px: 1.5,
                              }}
                            >
                              {row.unit_name || "—"}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                py: 0.8,
                                px: 1.5,
                                color: isOut
                                  ? PALETTE.error
                                  : isLow
                                    ? PALETTE.warning
                                    : PALETTE.success,
                              }}
                            >
                              {row.current_stock}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                fontSize: "0.72rem",
                                color: "text.secondary",
                                py: 0.8,
                                px: 1.5,
                              }}
                            >
                              {row.min_quantity}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.8, px: 1.5 }}>
                              <Chip
                                size="small"
                                label={
                                  isOut
                                    ? "Out of Stock"
                                    : isLow
                                      ? "Low Stock"
                                      : "Healthy"
                                }
                                sx={{
                                  height: 20,
                                  fontSize: "0.62rem",
                                  fontWeight: 700,
                                  bgcolor: isOut
                                    ? alpha(PALETTE.error, 0.1)
                                    : isLow
                                      ? alpha(PALETTE.warning, 0.1)
                                      : alpha(PALETTE.success, 0.1),
                                  color: isOut
                                    ? PALETTE.error
                                    : isLow
                                      ? PALETTE.warning
                                      : PALETTE.success,
                                  border: `1px solid ${
                                    isOut
                                      ? alpha(PALETTE.error, 0.25)
                                      : isLow
                                        ? alpha(PALETTE.warning, 0.25)
                                        : alpha(PALETTE.success, 0.25)
                                  }`,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </InfiniteScroll>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DashboardPage;
